import React, { useState } from "react";
import GlobalNav from "../components/GlobalNav";
import Footer from "../components/Footer";
import "../styles/buttons.css";
import "./DonatePage.css";

// Full content preserved from the original donate.handlebars, translated
// to the current theme -- nothing trimmed. Two things worth knowing:
//
// 1. The PayPal forms below use the ORIGINAL real hosted_button_id values
//    (8VAMJJFLGJCEW monthly, 2FAP4R529ANDC one-time). These are
//    self-contained PayPal buttons -- a plain form POST to paypal.com,
//    no API keys, no backend needed -- structurally different from the
//    donate page's old Stripe integration, which used a DIFFERENT
//    COMPANY's live key (Hype Machine) and was never usable. These
//    PayPal buttons were tied to the real MIHU account. I can't confirm
//    they're still active without an actual transaction, but there's no
//    evidence they belong to anyone else.
//
// 2. Images route through the direct Berkeley host URL rather than the
//    original's pixboost.com resizing proxy (that proxy's auth token has
//    very likely expired). Not confirmed these still resolve at all --
//    if they 404, they need re-uploading to this app's own image folder.

const GALLERY_IMAGES = [
  { src: "https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/images/fundraising/mihu-tour.png", alt: "MIHU Tour" },
  { src: "https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/images/fundraising/instrument-petting-zoo.jpg", alt: "Instrument Petting Zoo" },
  { src: "https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/images/fundraising/ucb-drum-circle.jpg", alt: "UC Berkeley Drum Circle" },
  { src: "https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/images/fundraising/momentum.png", alt: "Momentum" },
  { src: "https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/images/fundraising/capoeira.png", alt: "Capoeira" },
  { src: "https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/images/fundraising/agni-hotra.png", alt: "Agni Hotra" },
  { src: "https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/images/fundraising/utm-drum-circle.jpg", alt: "Unity Through Music Drum Circle" },
  { src: "https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/images/liveinstudio/liveinstudio0.png", alt: "Live in Studio" },
  { src: "https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/images/fundraising/wellness-conference.png", alt: "Wellness Conference" },
  { src: "https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/images/fundraising/mihu-workshop.jpg", alt: "MIHU Workshop" },
  { src: "https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/images/fundraising/urban-griots.png", alt: "Urban Griots" },
  { src: "https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/images/fundraising/musicishealing.png", alt: "Music Is Healing" },
];

export default function DonatePage() {
  const [contribType, setContribType] = useState("monthly"); // monthly | one-time
  const [showMorePerks, setShowMorePerks] = useState(false);

  const shareUrl = "https://www.musicishealing.us/donate";
  const facebookShareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
  const twitterShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
    "Support Music Is Healing - A platform for local artists, singers, and musicians"
  )}&url=${encodeURIComponent(shareUrl)}`;

  return (
    <div className="donate-page">
      <GlobalNav activeHref="/donate" ctaLabel="Join the collective" />

      <div className="donate-hero">
        <img
          className="donate-hero-image"
          src="https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/images/fundraising/pierre-mihu.png"
          alt=""
        />
        <div className="donate-hero-overlay">
          <h1>Support Music Is Healing</h1>
          <p>
            Music Is Healing has been building Unity through Music since
            2014. Help us become sustainable!
          </p>
        </div>
      </div>

      <div className="donate-main">
      <div className="donate-story-col">
        {/* <h2>Who is behind Music Is Healing?</h2>
        <p>
          Music Is Healing is a community learning organization and creative
          media services agency started by Pierre-Valery Tchetgen
          (Artist-Educator and Entrepreneur), while pursuing a PhD at the
          Graduate School of Education at UC Berkeley. Since 2006,
          Pierre-Valery (also known by the stage name{" "}
          <a href="http://akwerius.com" target="_blank" rel="noopener noreferrer">Akwerius</a>)
          has been amplifying creative voices and providing youth, families
          and the general public opportunities to harness their experiences
          and talents towards positive social change by connecting them with
          like-minded peers and mentors.
        </p>
        <p>
          The MusicIsHealing.Us platform is the fruit of our 5-year
          community initiative launched in 2014 involving poets, singers,
          healers and players of instruments of all ages committed to
          promote the message of Unity through Music. This network will
          serve to mobilize and connect our work across our nationwide (and
          soon-to-be global) chapters. We are open to students, artists,
          agriculturists, designers, dancers and music producers who believe
          in holistic wellness, creativity and technology as powerful tools
          for individual empowerment and sustainable community building.
        </p> */}

        <h2>What does Music Is Healing do?</h2>
        <p>
          Our goal is to build lasting connections between artists and the
          larger community through seasonal releases, online events and
          educational programs that promote the four pillars of wellness:
          Mind, Body, Community and Spirit. Music Is Healing creates
          year-round opportunities for artists, youth, families and elders.
          We do this through publications, workshops, conferences, concerts
          and nature retreats.
        </p>
        <p>
          By leveraging the power of long-distance collaboration via the
          web, our creative media production process is rooted in the
          healing arts (meditation, rhythm, movement) and the expressive
          arts (poetry, music, dance, arts) to promote individual
          creativity, collective leadership and build resilient communities.
        </p>
        <p>
          As a production collective, Music Is Healing offers a wide range
          of artistic media design and event production possibilities,
          thanks to long-lasting partnerships between artists in{" "}
          <a href="/registry">the crew</a>. As an online platform, our
          emphasis will be on special offerings for community members,
          exclusive tickets to concerts, classes and workshops as well as
          new releases (books, music, media) and lifestyle products
          (fashion, health). We will also feature local artists, vendors and
          businesses.
        </p>

        <h2>How did we get here?</h2>
        <img
          className="donate-story-img"
          src="https://wordsoundlife-berkeley-edu.apphost.ocf.berkeley.edu/images/fundraising/voicescamp.png"
          alt="Voices CAMP (Chicago, IL)"
        />
        <p>
          The seeds of Music Is Healing were planted during Summer 2006 in
          Chicago as the Voices CAMP program in collaboration with several
          community and school partners (After School Matters, Harold
          Washington College, University of Chicago, Voices of the New
          Millennium) to help address the lack in access to media-arts
          opportunities available to low-income youth. In the summer of
          2006, the Voices CAMP successfully engaged 18 Chicago youth in a
          six-week media-arts apprenticeship at Harold Washington College
          through a city grant from After School Matters. In 2008, the
          program (renamed iCAMP) won Best Program for the Downtown region
          for our collaboration with the Neighborhood Technology Resource
          Center and the{" "}
          <a href="https://wordsoundlife.berkeley.edu" target="_blank" rel="noopener noreferrer">
            Word.Sound.Life.
          </a>{" "}
          network was born of these efforts to mobilize the work of the
          organization to California, where it became established as a
          student organization on the UC Berkeley campus from 2007 to 2019.
        </p>
        <p>
          The Music Is Healing Us Tour was initially launched in 2014 at the
          Democracy Center in Boston as a project of Word. Sound. Life.
          after several artist members (singers, producers and musicians)
          decided to form an independent collective that would allow them
          to produce and license original music, art and film works; as
          well as collaborate on seasonal creative projects (mixtapes,
          concerts, podcasts, zines and films). The project has since
          relocated to Mount Shasta, CA where it formally became registered
          as a small business in 2018, after successfully hosting the{" "}
          <a href="/events">Unity through Music</a> grassroots festival for
          two consecutive years.
        </p>

        <h2>Why are we raising money?</h2>
        {/* <p>
          We're grateful that Music Is Healing has been able to touch so
          many lives over the years. We may seem like a tag team of
          drifters from the outside, but our potential stems from a broad
          network of international connections and a track-record of
          professional experiences in the healing arts, performing arts,
          education, media and technology sectors. Our goal is to harness
          our collective energies to share our individual gifts of healing
          through mindfulness, writing, storytelling, musicmaking,
          stage-acting and mediamaking with others (especially youth,
          families and elders), that they may (re)discover theirs and
          (re)gain control of their destinies.
        </p>
        <p>
          Today, young people in urban environments often have an abundance
          of creativity as a result of their surroundings, without proper
          channels to express such. Thus, as a learning organization and
          creative media agency, our daily practice is motivated by this
          chronic lack of access low-income youth of color have to digital
          resources and trained educators who can productively use them.
          The Voices Internet Community Arts and Media Partnerships (Voices
          iCAMP) was created as a curriculum model to develop youth
          capacities around digital media and technology by integrating
          critical tools and practices for a more self-directed, and
          reflective, mode of learning. With a special focus placed on
          communication, analytic thinking and the role and power of new
          media technologies, the curriculum model creates a culture in
          which participants are trained to learn and use digital media and
          communications technologies to produce cultural media or creative
          narrations that address or study an issue or topic of concern to
          them (and their peers).
        </p> */}
        <p>
          Your monthly contributions will allow us to keep the Music Is
          Healing projects and website operational, but more importantly,
          it will make it possible for us to continue creating more
          enrichment and employment opportunities for teens by recruiting
          and training artists and college students as youth mentors and
          guides. We've been refining these ideas for over 10 years and
          we're ready to make them a reality, with your help!
        </p>

        <h2>What is our vision of the future?</h2>
        <p>
          We have several projects in the works! We plan to offer several
          public services to the community, business and not-for-profit
          clients. Some of these services include:
        </p>
        <ul className="donate-vision-list">
          <li>Daily Streams (Creativity, Mindfulness, Wellness)</li>
          <li>Weekly Classes &amp; Workshops (Instructor-led)</li>
          <li>Live Broadcasts &amp; Webinars</li>
          <li>Community Events (Online)</li>
          <li>Podcasts and more</li>
        </ul>
      </div>

        <div className="donate-sidebar-col">
          <div className="donate-give-col">
          <div className="donate-toggle">
            <button
              className={contribType === "monthly" ? "active" : ""}
              onClick={() => setContribType("monthly")}
            >
              Monthly
            </button>
            <button
              className={contribType === "one-time" ? "active" : ""}
              onClick={() => setContribType("one-time")}
            >
              One-time
            </button>
          </div>

          <p className="donate-intro">
            Our service runs on member, supporter and sponsor contributions.
            Your contributions:
          </p>

          {contribType === "monthly" ? (
            <ul className="donate-benefits">
              <li>Keep Music Is Healing running</li>
              <li>Help us produce media content <small>(music, videos, zines)</small></li>
              <li>Pay artist mentors and youth apprentices <small>(camps, retreats)</small></li>
              <li>
                <button className="donate-perks-toggle" onClick={() => setShowMorePerks((v) => !v)}>
                  {showMorePerks ? "Hide" : "More"} membership perks
                </button>
              </li>
              {showMorePerks && (
                <li className="donate-perks-detail">
                  Community Boards<br />
                  Member-only Videos<br />
                  Full Stream archives<br />
                  Infoline <small>(call-in # for podcast contributions)</small><br />
                  Chatroom <small>(connect with others in real time)</small><br />
                  Discount tickets (online classes, events)
                </li>
              )}
              <li>$7+ comes with a zine and t-shirt</li>
            </ul>
          ) : (
            <ul className="donate-benefits">
              <li>Keep Music Is Healing running</li>
              <li>Help us produce media content (music, videos, zines)</li>
              <li>Pay artist mentors and youth apprentices</li>
              <li>
                <button className="donate-perks-toggle" onClick={() => setShowMorePerks((v) => !v)}>
                  {showMorePerks ? "Hide" : "More"} supporter perks for a year
                </button>
              </li>
              {showMorePerks && (
                <li className="donate-perks-detail">
                  Community Boards<br />
                  Member-only Videos<br />
                  Full Stream archives<br />
                  Infoline (access # for podcast contributions)<br />
                  Chatroom (connect with others in real time)<br />
                  Discount tickets (online classes, events)
                </li>
              )}
              <li>$77+ comes with a zine and t-shirt</li>
            </ul>
          )}

          {/* Real PayPal hosted buttons -- self-contained, no API keys needed */}
          {contribType === "monthly" ? (
            <form
              action="https://www.paypal.com/cgi-bin/webscr"
              method="post"
              target="_blank"
              className="donate-paypal-form"
            >
              <input type="hidden" name="cmd" value="_s-xclick" />
              <input type="hidden" name="hosted_button_id" value="8VAMJJFLGJCEW" />
              <label className="donate-tier-select-label" htmlFor="os0">Levels of Support</label>
              <select name="os0" id="os0" className="donate-tier-select">
                <option value="400">Bronze : $4.00 USD - monthly</option>
                <option value="700">Silver : $7.00 USD - monthly</option>
                <option value="900">Gold : $9.00 USD - monthly</option>
              </select>
              <input type="hidden" name="currency_code" value="USD" />
              <button type="submit" className="mihu-btn-primary-lg donate-paypal-submit">
                Subscribe monthly with PayPal
              </button>
            </form>
          ) : (
            <div className="donate-onetime-tiers">
              <div className="donate-tier-labels">
                <span>$44</span><span>$77</span><span>$99</span>
              </div>
              <form
                action="https://www.paypal.com/cgi-bin/webscr"
                method="post"
                target="_blank"
                className="donate-paypal-form"
              >
                <input type="hidden" name="cmd" value="_s-xclick" />
                <input type="hidden" name="hosted_button_id" value="2FAP4R529ANDC" />
                <button type="submit" className="mihu-btn-primary-lg donate-paypal-submit">
                  Donate with PayPal
                </button>
              </form>
            </div>
          )}

          <div className="donate-share">
            <span>Know anyone creative? Tell them about this campaign!</span>
            <div className="donate-share-buttons">
              <a href={facebookShareUrl} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook">
                Facebook
              </a>
              <a href={twitterShareUrl} target="_blank" rel="noopener noreferrer" aria-label="Share on Twitter">
                Twitter
              </a>
            </div>
          </div>

          <div className="donate-patreon-note">
            <p>Prefer Patreon? We're also there, with three membership tiers.</p>
            <a
              className="mihu-btn-secondary-lg"
              href="https://www.patreon.com/cw/musicishealing"
              target="_blank"
              rel="noopener noreferrer"
            >
              View us on Patreon
            </a>
          </div>
        </div>

        <div className="donate-gallery-col">
          {GALLERY_IMAGES.map((img) => (
            <img key={img.src} src={img.src} alt={img.alt} className="donate-gallery-img" />
          ))}
        </div>
      </div>

      </div>
      <Footer />
    </div>
  );
}
